
import { supabase } from "@/integrations/supabase/client";

export interface CPVTreeNode {
  cpv_code: string;
  beskrivelse: string;
  level: 'Division' | 'Group' | 'Class' | 'Category' | 'Subcategory';
  children?: CPVTreeNode[];
  parent_code?: string;
  isExpanded?: boolean;
}

/**
 * Fetches CPV tree nodes by parent code
 * @param parentCode The parent CPV code or null for top-level divisions
 * @returns Array of CPV tree nodes
 */
export const fetchCPVTreeNodesByParent = async (
  parentCode: string | null
): Promise<CPVTreeNode[]> => {
  try {
    let query = supabase
      .from("cpv_tree")
      .select("*")
      .order("cpv_code");
    
    if (parentCode === null) {
      query = query.is("parent_code", null);
    } else {
      query = query.eq("parent_code", parseInt(parentCode));
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching CPV tree nodes:", error);
      return [];
    }

    return (data || []).map((node) => ({
      cpv_code: node.cpv_code.toString(),
      beskrivelse: node.beskrivelse,
      level: node.level as 'Division' | 'Group' | 'Class' | 'Category' | 'Subcategory',
      parent_code: node.parent_code ? node.parent_code.toString() : undefined,
      children: [],
      isExpanded: false
    }));
  } catch (error) {
    console.error("Error in fetchCPVTreeNodesByParent:", error);
    return [];
  }
};

/**
 * Fetches top-level divisions from the CPV tree
 * @returns Array of division nodes
 */
export const fetchCPVDivisions = async (): Promise<CPVTreeNode[]> => {
  return fetchCPVTreeNodesByParent(null);
};

/**
 * Gets the full path to a CPV node
 * @param cpvCode The CPV code to find the path for
 * @returns Array of CPV codes representing the path
 */
export const getCPVPathForCode = async (cpvCode: string): Promise<string[]> => {
  const path: string[] = [];
  let currentCode = cpvCode;
  
  while (currentCode) {
    path.unshift(currentCode);
    
    const { data, error } = await supabase
      .from("cpv_tree")
      .select("parent_code")
      .eq("cpv_code", parseInt(currentCode))
      .single();
    
    if (error || !data.parent_code) break;
    currentCode = data.parent_code.toString();
  }
  
  return path;
};

/**
 * Fetches a CPV tree node by its code
 * @param cpvCode The CPV code to fetch
 * @returns The CPV tree node or null if not found
 */
export const fetchCPVNodeByCode = async (cpvCode: string): Promise<CPVTreeNode | null> => {
  try {
    // First try exact match by code
    const { data, error } = await supabase
      .from("cpv_tree")
      .select("*")
      .eq("cpv_code", parseInt(cpvCode))
      .single();

    if (!error && data) {
      return {
        cpv_code: data.cpv_code.toString(),
        beskrivelse: data.beskrivelse,
        level: data.level as 'Division' | 'Group' | 'Class' | 'Category' | 'Subcategory',
        parent_code: data.parent_code ? data.parent_code.toString() : undefined,
        children: [],
        isExpanded: false
      };
    }

    // If not found, try the CPV Table
    const { data: cpvTableData, error: cpvTableError } = await supabase
      .from("CPV Table")
      .select("CPV, Beskrivelse")
      .eq("CPV", parseInt(cpvCode))
      .single();

    if (!cpvTableError && cpvTableData) {
      return {
        cpv_code: cpvTableData.CPV.toString(),
        beskrivelse: cpvTableData.Beskrivelse,
        level: 'Category', // Assuming a default level
        children: [],
        isExpanded: false
      };
    }

    // If still not found, return null
    return null;
  } catch (error) {
    console.error("Error in fetchCPVNodeByCode:", error);
    return null;
  }
};
